import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError } from "../api/client";
import { ME_KEY } from "../api/hooks/useMe";
import type { Me } from "../api/types";

// The password step resolves to a real session OR, when the account has an
// enabled TOTP factor, a short-lived challenge to redeem at the TOTP step.
export type SignInResult =
  | { readonly kind: "session"; readonly user: Me }
  | { readonly kind: "mfa"; readonly mfaToken: string };

interface SignInResponse {
  readonly user?: Me;
  readonly mfaRequired?: boolean;
  readonly mfaToken?: string;
}

export function useSignIn() {
  const qc = useQueryClient();
  return useMutation<SignInResult, ApiError, { email: string; password: string }>({
    mutationFn: async (input) => {
      const res = await apiFetch<SignInResponse>("/auth/sign-in", {
        method: "POST",
        body: input,
      });
      if (res.mfaRequired && res.mfaToken) return { kind: "mfa", mfaToken: res.mfaToken };
      return { kind: "session", user: res.user! };
    },
    onSuccess: (result) => {
      if (result.kind !== "session") return;
      qc.setQueryData(ME_KEY, result.user);
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}
